import React, { Suspense, lazy, useState, useEffect } from 'react';
import { BrowserRouter as Router, Switch, Route, Redirect, useLocation } from 'react-router-dom';
import PageScrollTop from './components/pageToTop/PageScrollTop';
import 'bootstrap/dist/css/bootstrap.min.css';
import { BrandThemeProvider } from './data/brandTheme/BrandThemeContext';
import RouteTransition from './features/shared/RouteTransition';
import HeaderTopNews from './layout/header/HeaderTopNews';
import './assets/scss/style.scss';
import './assets/css/ti-primary-cta.css';
import './assets/css/ti-buttons.css';
import './assets/css/ti-section-adapt.css';
import './assets/css/page-transition.css';
import './assets/css/ti-mode-overrides.css';
import './assets/css/ti-corner-style.css';
import './assets/css/ti-accented-text.css';
import './assets/css/ti-shell.css';
import { useCopyTracking } from './features/analytics/useCopyTracking';
import { useTiClickTracking } from './features/analytics/useTiClickTracking';
import AnalyticsListener from './features/analytics/AnalyticsListener';
import CookieBanner from './features/consent/CookieBanner';
import { initAnalyticsContext } from './features/analytics/analytics';
import { detectLocale, basenameForLocale } from './i18n/locale';
import { LocaleProvider } from './i18n/LocaleContext';
import { t } from './i18n/runtime';

// Main site routes — lazy so each page chunk is only fetched when navigated to,
// keeping the initial app shell as small as possible.
const ProductLandingPage = lazy(() => import("./pages/product/ProductLandingPage"));
const HomePage = lazy(() => import("./pages/HomePage"));
const CategoryPage = lazy(() => import("./pages/CategoryPage"));
const AllToolsPage = lazy(() => import("./pages/AllToolsPage"));
const FindYourToolsPage = lazy(() => import("./pages/FindYourToolsPage"));
const LibraryPage = lazy(() => import("./pages/LibraryPage"));
const ProductsOverviewPage = lazy(() => import("./pages/ProductsOverviewPage"));
const CommunityPage = lazy(() => import("./pages/CommunityPage"));
const FundedPage = lazy(() => import("./pages/FundedPage"));
const LunchAndLearnLinksPage = lazy(() => import("./pages/LunchAndLearnLinksPage"));
const PowerOfThreePage = lazy(() => import("./pages/learn/PowerOfThreePage"));
const VideoSearchPage = lazy(() => import("./pages/VideoSearchPage"));
const Error = lazy(() => import("./pages/Error"));
const PowerScanningPage = lazy(() => import("./pages/PowerScanningPage"));
const PowerScanningPage2 = lazy(() => import("./pages/PowerScanningPage2"));
const PowerScanningPageLight = lazy(() => import("./pages/PowerScanningPageLight"));
const PowerScanningPageLight2 = lazy(() => import("./pages/PowerScanningPageLight2"));
const PowerScanningPageLight3 = lazy(() => import("./pages/PowerScanningPageLight3"));
const SpinToWinPage = lazy(() => import("./features/spinWheel/SpinToWinPage"));
const BonusPage = lazy(() => import("./pages/BonusPage"));
const ShowLoop = lazy(() => import("./features/bonus/ShowLoop"));

// Long-form theme-residue pages (legal + checkout) statically pull in heavy CJS
// deps — 3d-react-carousal (GSAP Draggable) and react-fluid-animation — whose
// UMD wrappers crash the main bundle at eval with "exports is not defined".
// Lazy-load them so those deps live in their own chunks, off the app shell.
const Disclaimer = lazy(() => import("./pages/legal/Disclaimer"));
const PrivacyPolicy = lazy(() => import("./pages/legal/PrivacyPolicy"));
const RefundPolicy = lazy(() => import("./pages/legal/RefundPolicy"));
const SuccessfulPayment = lazy(() => import("./pages/checkout/SuccessfulPayment"));
const TermsOfService = lazy(() => import("./pages/legal/TermsOfService"));
const ContactPage = lazy(() => import("./pages/ContactPage"));
const Imprint = lazy(() => import("./pages/legal/Imprint"));
const RightOfWithdrawal = lazy(() => import("./pages/legal/RightOfWithdrawal"));
const CookiePolicy = lazy(() => import("./pages/legal/CookiePolicy"));

// Paths where the promo banner is always suppressed (standalone landing pages).
const BANNER_SUPPRESSED_PATHS = ['/power-scanning', '/spin', '/loop'];

function PromoBanner() {
  const { pathname } = useLocation();
  const [config, setConfig] = useState(null);

  useEffect(() => {
    fetch('/api/banner')
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => setConfig(data))
      .catch(() => {});
  }, []);

  if (!config || !config.enabled) return null;
  if (BANNER_SUPPRESSED_PATHS.includes(pathname)) return null;

  const text = (
    <>
      {config.textBefore ? <>{config.textBefore}{' '}</> : null}
      {config.textBold ? <strong>{config.textBold}</strong> : null}
      {config.textAfter ? <>{' '}{config.textAfter}</> : null}
    </>
  );

  return (
    <HeaderTopNews
      badge={config.badge}
      text={text}
      code={config.code || undefined}
      ctaLabel={config.ctaLabel}
      ctaHref={config.ctaHref}
      ctaExternal={!!config.ctaExternal}
      theme={config.theme || 'dark'}
    />
  );
}

const App = () => {
    useCopyTracking();
    useTiClickTracking();

    // Locale is fixed for the lifetime of the document. Router `basename` is the
    // whole trick: with basename="/de", every existing <Link to="/trendline-iq">
    // in the codebase resolves to /de/trendline-iq with no edits, and
    // useLocation() still reports the un-prefixed path so active-link checks in
    // Nav.js keep working unchanged.
    const locale = detectLocale(window.location.pathname);

    initAnalyticsContext({ locale });

    if (typeof document !== 'undefined') {
        document.documentElement.lang = locale;
    }

    return (
        <LocaleProvider locale={locale}>
        <BrandThemeProvider>
        <Router basename={basenameForLocale(locale)}>
            <PageScrollTop>
                <Suspense fallback={null}>
                <AnalyticsListener />
                {/* Site-wide promo banner — config fetched from /api/banner on mount.
                    Toggle it live in /admin without a redeploy.
                    PromoBanner renders nothing when disabled or on suppressed paths. */}
                <PromoBanner />
                <Switch>
                    <Route path={process.env.PUBLIC_URL + "/"} exact component={HomePage} />
                    {/* /products now lands on the catalogue; AllProductsPage is retired. */}
                    <Redirect exact from={process.env.PUBLIC_URL + "/products"} to={process.env.PUBLIC_URL + "/catalogue"} />
                    <Route path={process.env.PUBLIC_URL + "/all-tools"} exact component={AllToolsPage} />
                    <Route path={process.env.PUBLIC_URL + "/catalogue"} exact component={LibraryPage} />
                    <Route path={process.env.PUBLIC_URL + "/highlights"} exact component={ProductsOverviewPage} />
                    <Route path={process.env.PUBLIC_URL + "/category/:slug"} exact component={CategoryPage} />
                    <Route path={process.env.PUBLIC_URL + "/find-your-tools"} exact component={FindYourToolsPage} />
                    <Route path={process.env.PUBLIC_URL + "/community"} exact component={CommunityPage} />
                    <Route path={process.env.PUBLIC_URL + "/tradefundrr"} exact component={FundedPage} />
                    <Route path={process.env.PUBLIC_URL + "/lunch-and-learn-links"} exact component={LunchAndLearnLinksPage} />
                    <Route path={process.env.PUBLIC_URL + "/learn/power-of-three"} exact component={PowerOfThreePage} />
                    <Route path={process.env.PUBLIC_URL + "/videos"} exact component={VideoSearchPage} />

                    {/* Product PDPs */}
                    <Route path={process.env.PUBLIC_URL + "/liquidity-iq"} exact render={() => <ProductLandingPage slug="liquidity-iq" />} />
                    <Route path={process.env.PUBLIC_URL + "/liquidity-iq-1"} exact render={() => <ProductLandingPage slug="liquidity-iq-1" />} />
                    <Route path={process.env.PUBLIC_URL + "/liquidity-iq-2"} exact render={() => <ProductLandingPage slug="liquidity-iq-2" />} />
                    <Route path={process.env.PUBLIC_URL + "/volume-iq"} exact render={() => <ProductLandingPage slug="volume-iq" />} />
                    <Route path={process.env.PUBLIC_URL + "/ma-rush"} exact render={() => <ProductLandingPage slug="ma-rush" />} />
                    <Route path={process.env.PUBLIC_URL + "/liquidity-view-scanner"} exact render={() => <ProductLandingPage slug="liquidity-view" />} />
                    <Route path={process.env.PUBLIC_URL + "/trendline-iq"} exact render={() => <ProductLandingPage slug="trendline-iq" />} />
                    <Route path={process.env.PUBLIC_URL + "/trend-radar"} exact render={() => <ProductLandingPage slug="trend-radar" />} />
                    <Route path={process.env.PUBLIC_URL + "/gap-iq"} exact render={() => <ProductLandingPage slug="gap-iq" />} />
                    <Route path={process.env.PUBLIC_URL + "/one-iq"} exact render={() => <ProductLandingPage slug="one-iq" />} />
                    <Route path={process.env.PUBLIC_URL + "/supertrend-bulls-eye"} exact render={() => <ProductLandingPage slug="supertrend-bulls-eye" />} />
                    <Route path={process.env.PUBLIC_URL + "/1-2-3-strike"} exact render={() => <ProductLandingPage slug="1-2-3-strike" />} />
                    <Route path={process.env.PUBLIC_URL + "/ti-matrix"} exact render={() => <ProductLandingPage slug="ti-matrix" />} />
                    <Route path={process.env.PUBLIC_URL + "/ti-pairs"} exact render={() => <ProductLandingPage slug="ti-pairs" />} />
                    <Route path={process.env.PUBLIC_URL + "/strategy-builder"} exact render={() => <ProductLandingPage slug="strategy-builder" />} />
                    <Route path={process.env.PUBLIC_URL + "/portfolio-architect"} exact render={() => <ProductLandingPage slug="portfolio-architect" />} />
                    <Route path={process.env.PUBLIC_URL + "/portfolio-tester"} exact render={() => <ProductLandingPage slug="portfolio-tester" />} />
                    <Route path={process.env.PUBLIC_URL + "/divergence-cloud"} exact render={() => <ProductLandingPage slug="divergence-cloud" />} />
                    <Route path={process.env.PUBLIC_URL + "/super-signals-premium"} exact render={() => <ProductLandingPage slug="super-signals-premium" />} />
                    <Route path={process.env.PUBLIC_URL + "/volatility-crusher"} exact render={() => <ProductLandingPage slug="volatility-crusher" />} />
                    <Route path={process.env.PUBLIC_URL + "/iq-core-pack"} exact render={() => <ProductLandingPage slug="iq-core-pack" />} />
                    <Route path={process.env.PUBLIC_URL + "/trend-trading-pack"} exact render={() => <ProductLandingPage slug="trend-trading-pack" />} />
                    <Route path={process.env.PUBLIC_URL + "/market-sentiment-pack"} exact render={() => <ProductLandingPage slug="market-sentiment-pack" />} />
                    <Route path={process.env.PUBLIC_URL + "/welcome-package"} exact render={() => <ProductLandingPage slug="welcome-package" />} />
                    <Route path={process.env.PUBLIC_URL + "/power-bundle-360"} exact render={() => <ProductLandingPage slug="power-bundle-360" />} />
                    <Route path={process.env.PUBLIC_URL + "/prop-iq"} exact render={() => <ProductLandingPage slug="prop-iq" />} />
                    <Route path={process.env.PUBLIC_URL + "/volume-scan-pro"} exact render={() => <ProductLandingPage slug="volume-scan-pro" />} />

                    {/* Landing pages — no Layout wrapper */}
                    <Route path={process.env.PUBLIC_URL + "/power-scanning"} exact component={PowerScanningPageLight3} />
                    <Route path={process.env.PUBLIC_URL + "/power-scanning-2"} exact component={PowerScanningPage2} />
                    <Route path={process.env.PUBLIC_URL + "/power-scanning-light"} exact component={PowerScanningPageLight} />
                    <Route path={process.env.PUBLIC_URL + "/power-scanning-light-2"} exact component={PowerScanningPageLight2} />
                    <Route path={process.env.PUBLIC_URL + "/power-scanning-light-3"} exact component={PowerScanningPageLight3} />
                    <Route path={process.env.PUBLIC_URL + "/spin"} exact component={SpinToWinPage} />
                    {/* Finance 2026 Frankfurt. These four are the URLs on the
                        printed QR codes; server/localeLanding.js sends them to
                        the German tree unless the visitor picked English.
                        /vorteil is the URL on the current Market Edge 360 card
                        and /messe the one on the first print run — same page,
                        both kept live because both are out in the world. */}
                    <Route path={process.env.PUBLIC_URL + "/messe"} exact render={() => <ProductLandingPage slug="market-edge-360" />} />
                    <Route path={process.env.PUBLIC_URL + "/vorteil"} exact render={() => <ProductLandingPage slug="market-edge-360" />} />
                    <Route path={process.env.PUBLIC_URL + "/zeit"} exact render={() => <ProductLandingPage slug="liquidity-view-finance26" />} />
                    <Route path={process.env.PUBLIC_URL + "/bonus"} exact component={BonusPage} />
                    {/* /loop — the Finance26 booth screen. Full-screen, self-running; staff
                        open /de/loop on the stand machine. No header/footer by design. */}
                    <Route path={process.env.PUBLIC_URL + "/loop"} exact component={ShowLoop} />

                    {/* Legal & checkout */}
                    <Route path={process.env.PUBLIC_URL + "/disclaimer"} exact component={Disclaimer} />
                    <Route path={process.env.PUBLIC_URL + "/error"} exact component={Error} />
                    <Route path={process.env.PUBLIC_URL + "/privacy-policy"} exact component={PrivacyPolicy} />
                    <Route path={process.env.PUBLIC_URL + "/refund-policy"} exact component={RefundPolicy} />
                    <Route path={process.env.PUBLIC_URL + "/payment-successful"} exact component={SuccessfulPayment} />
                    <Route path={process.env.PUBLIC_URL + "/terms-of-service"} exact component={TermsOfService} />
                    <Route path={process.env.PUBLIC_URL + "/contact"} exact component={ContactPage} />
                    <Route path={process.env.PUBLIC_URL + "/imprint"} exact component={Imprint} />
                    <Route path={process.env.PUBLIC_URL + "/right-of-withdrawal"} exact component={RightOfWithdrawal} />
                    <Route path={process.env.PUBLIC_URL + "/cookie-policy"} exact component={CookiePolicy} />

                    {/* German-language aliases. German visitors (and German
                        search results) look for "Impressum" and "Widerrufsrecht"
                        by name, but the canonical URL stays the English slug so
                        that the hreflang pairing in SEO.js — which just prefixes
                        /de — keeps resolving to a real page. These redirect
                        rather than render, so there is no duplicate content. */}
                    <Redirect exact from={process.env.PUBLIC_URL + "/impressum"} to={process.env.PUBLIC_URL + "/imprint"} />
                    <Redirect exact from={process.env.PUBLIC_URL + "/widerruf"} to={process.env.PUBLIC_URL + "/right-of-withdrawal"} />
                    <Redirect exact from={process.env.PUBLIC_URL + "/widerrufsrecht"} to={process.env.PUBLIC_URL + "/right-of-withdrawal"} />
                    <Redirect exact from={process.env.PUBLIC_URL + "/datenschutz"} to={process.env.PUBLIC_URL + "/privacy-policy"} />
                    <Redirect exact from={process.env.PUBLIC_URL + "/agb"} to={process.env.PUBLIC_URL + "/terms-of-service"} />
                </Switch>
                </Suspense>
            </PageScrollTop>
            <RouteTransition />
            {/* Rendered inside <Router> because the banner links to the cookie
                and privacy pages with <Link>, which needs the router context to
                pick up the /de basename. */}
            <CookieBanner />
        </Router>
        </BrandThemeProvider>
        </LocaleProvider>
    );
};

export default App;

